import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Search, ShoppingBag, PackageSearch, LayoutDashboard } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useApi } from "@/lib/useApi";

const AdminLink = () => {
  const call = useApi();
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => { call("get", "/me").then((m) => setIsAdmin(m.role === "admin")).catch(() => setIsAdmin(false)); }, [call]);
  if (!isAdmin) return null;
  return (
    <Link to="/admin" data-testid="nav-admin-link" className="rounded-lg p-2 text-stone-500 hover:bg-stone-100" title="Panel Admin">
      <LayoutDashboard className="h-5 w-5" />
    </Link>
  );
};

const Logo = () => (
  <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 shrink-0">
    <span className="grid h-9 w-9 place-items-center rounded-full border-2 border-dashed border-amber-700 text-amber-700 font-bold text-sm">EM</span>
    <span className="leading-tight">
      <span className="block font-semibold text-stone-900">EllMom</span>
      <span className="block text-xs text-stone-500">Handmade</span>
    </span>
  </Link>
);

export const Header = () => {
  const { count, setOpen } = useCart();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/?q=${encodeURIComponent(q.trim())}#katalog` : "/#katalog");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 md:gap-6 md:px-6">
        <Logo />
        <form onSubmit={submit} className="relative hidden flex-1 md:block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cari tas rajut, benang, clay..." className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 pl-9 pr-3 text-sm outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-700/20" />
        </form>
        <nav className="ml-auto flex items-center gap-1 md:gap-2">
          <NavLink to="/lacak" data-testid="nav-track-link" className="flex items-center gap-1.5 rounded-lg p-2 text-sm text-stone-600 hover:bg-stone-100 sm:px-3" title="Lacak Pesanan">
            <PackageSearch className="h-5 w-5 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Lacak Pesanan</span>
          </NavLink>
          <Show when="signed-in">
            <NavLink to="/pesanan-saya" data-testid="nav-my-orders-link" className="hidden rounded-lg px-3 py-2 text-sm text-stone-600 hover:bg-stone-100 lg:block">Pesanan Saya</NavLink>
            <AdminLink />
          </Show>
          <button data-testid="header-cart-button" onClick={() => setOpen(true)} className="relative rounded-lg p-2 text-stone-700 hover:bg-stone-100" aria-label="Keranjang">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="header-cart-count" className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-amber-700 px-1 text-[11px] font-semibold text-white">{count}</span>
            )}
          </button>
          <Show when="signed-out">
            <Link to="/sign-in" data-testid="header-signin-link" className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-medium hover:bg-stone-100">Masuk</Link>
          </Show>
          <Show when="signed-in"><UserButton /></Show>
        </nav>
      </div>
      <form onSubmit={submit} className="relative px-4 pb-3 md:hidden">
        <Search className="pointer-events-none absolute left-7 top-[18px] h-4 w-4 -translate-y-1/2 text-stone-400" />
        <input data-testid="header-search-input-mobile" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cari produk..." className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 pl-9 pr-3 text-sm outline-none focus:border-amber-700" />
      </form>
    </header>
  );
};
